import { inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { AuthService } from "@services/auth/auth.service";
import { catchError, map, of } from "rxjs";

interface RouteRoleConfig {
  roles?: string[];
  requireAll?: boolean; // true = needs all roles, false = needs any role
  redirectTo?: string; // custom redirect path
};

export const canActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  return authService.checkLogin().pipe(
    map((isLoggedIn: boolean) => {
      if (!isLoggedIn) {
        // Redirect directly to Keycloak, storing the intended destination
        // as the returnUrl that will be restored after callback.
        authService.login(state.url);
        return false;
      }

      if (authService.isSuperAdmin()) {
        return true;
      }

      const roleConfig: RouteRoleConfig = {
        roles: route.data?.['roles'] as string[],
        requireAll: route.data?.['requireAll'] as boolean || false,
        redirectTo: route.data?.['redirectTo'] as string || '/access-denied'
      };
      
      if (!roleConfig.roles || roleConfig.roles.length === 0) {
        return true;
      }

      const userRoles = authService.getRolesByToken() || [];
      
      const hasAccess = roleConfig.requireAll 
        ? hasAllRoles(userRoles, roleConfig.roles, authService)
        : hasAnyRole(userRoles, roleConfig.roles, authService);

      if (hasAccess) {
        return true;
      }

      return router.createUrlTree([roleConfig.redirectTo]);
    }),
    catchError(() => {
      authService.login(state.url);
      return of(false);
    })
  );
};

export const AuthGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => canActivate(route, state);

const matchesRequiredRole = (requiredRole: string, userRoles: string[], authService: AuthService): boolean => {
  if (requiredRole === 'ADM') {
    return authService.isAdmin();
  }

  return userRoles.includes(requiredRole);
};

const hasAnyRole = (userRoles: string[], requiredRoles: string[], authService: AuthService): boolean => {
  return requiredRoles.some(role => matchesRequiredRole(role, userRoles, authService));
};

const hasAllRoles = (userRoles: string[], requiredRoles: string[], authService: AuthService): boolean => {
  return requiredRoles.every(role => matchesRequiredRole(role, userRoles, authService));
};

export { hasAnyRole, hasAllRoles };